import { useMemo } from "react";
import { useStore } from "../state/store";
import type { EngineView } from "../engine/compute";
import type { ChainResult } from "../engine/probability";
import { gradeGuidance } from "../engine/liveGuidance";

function probColor(pct: number): string {
  return pct >= 20 ? "#22DD66" : pct >= 10 ? "#FBBF24" : "#60A5FA";
}

// Small-sample guardrail (carried over from UNIFIED_CANVAS_PLAN.md).
function SampleWarn({ n }: { n: number }) {
  if (n >= 10) return null;
  return (
    <div style={{ color: "#FBBF24", fontSize: 11, marginTop: 4 }}>
      ⚠ Small sample (n={n}) — interpret probabilities with caution.
    </div>
  );
}

function Chain2({ c }: { c: ChainResult }) {
  return (
    <div>
      <div className="prob-hero">
        <div className="muted sm">P(B | A)</div>
        <div className="prob-num" style={{ color: probColor(c.prob_b_given_a_pct) }}>
          {c.prob_b_given_a_pct}%
        </div>
        <div className="muted sm">
          {c.count_ab} of {c.count_a} storms
        </div>
        <SampleWarn n={c.count_a} />
      </div>
      <div className="row">
        <div className="metric">
          <div className="muted xs">BOX A</div>
          🌀 {c.breakdown_a.HU} · 🌊 {c.breakdown_a.TS} · ☁️ {c.breakdown_a.TD}
        </div>
        <div className="metric">
          <div className="muted xs">A → B</div>
          🌀 {c.breakdown_ab.HU} · 🌊 {c.breakdown_ab.TS} · ☁️ {c.breakdown_ab.TD}
        </div>
      </div>
      <div className="row">
        <div className="metric">
          <div className="muted xs">Box A freq</div>
          {c.annual_frequency_a}/yr
        </div>
        <div className="metric">
          <div className="muted xs">A→B freq</div>
          {c.annual_frequency_ab}/yr
        </div>
      </div>
    </div>
  );
}

function Chain3({ c }: { c: ChainResult }) {
  return (
    <div>
      <div className="prob-hero" style={{ display: "flex", gap: 16, justifyContent: "space-around" }}>
        <div>
          <div className="muted sm">P(B | A)</div>
          <div className="prob-num" style={{ color: probColor(c.prob_b_given_a_pct) }}>
            {c.prob_b_given_a_pct}%
          </div>
          <div className="muted sm">{c.count_ab} of {c.count_a}</div>
        </div>
        <div>
          <div className="muted sm">P(C | A∩B)</div>
          <div className="prob-num" style={{ color: probColor(c.prob_c_given_ab_pct ?? 0) }}>
            {c.prob_c_given_ab_pct ?? 0}%
          </div>
          <div className="muted sm">{c.count_abc} of {c.count_ab}</div>
        </div>
      </div>
      <SampleWarn n={c.count_ab} />
      <div className="row">
        <div className="metric"><div className="muted xs">A freq</div>{c.annual_frequency_a}/yr</div>
        <div className="metric"><div className="muted xs">A→B freq</div>{c.annual_frequency_ab}/yr</div>
        <div className="metric"><div className="muted xs">A→B→C freq</div>{c.annual_frequency_abc}/yr</div>
      </div>
    </div>
  );
}

interface Props {
  view: EngineView;
  hasBoxA: boolean;
  hasBoxB: boolean;
  oniNote: string | null;
}

// Feature B, graded: how does TODAY'S guidance compare with the historical
// base rate for the same boxes? Ensemble members give the dynamical strike
// fraction; the chain result gives the climatological one.
function LiveGuidanceBlock() {
  const climoOverlay = useStore((s) => s.climoOverlay);
  const bundle = useStore((s) => s.live.bundle);
  const boxA = useStore((s) => s.boxA);
  const boxB = useStore((s) => s.boxB);

  const grade = useMemo(
    () => (climoOverlay && bundle && boxA ? gradeGuidance(bundle, boxA, boxB) : null),
    [climoOverlay, bundle, boxA, boxB],
  );
  if (!grade || (grade.ensembles.length === 0 && !grade.aids)) return null;

  const a = grade.aids;
  return (
    <div className="metric" style={{ marginTop: 10 }}>
      <div className="muted xs">🛰 LIVE GUIDANCE — {bundle!.name}</div>
      {grade.ensembles.map((e) => (
        <div style={{ fontSize: 13, marginTop: 4 }} key={e.source}>
          {e.source}{e.kind === "ai" ? " (AI)" : ""}:{" "}
          <b style={{ color: probColor(e.pctA) }}>{e.hitA}/{e.total} members ({e.pctA}%)</b> through Box A
          {e.medianTauA != null && <span className="muted"> · median arrival τ+{e.medianTauA}h</span>}
          {e.hitAB != null && e.hitA > 0 && (
            <div>→ of those, <b style={{ color: probColor(e.pctAB ?? 0) }}>{e.hitAB} ({e.pctAB}%)</b> continue into Box B</div>
          )}
        </div>
      ))}
      {a && a.total > 0 && (
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          Aids: {a.hitA}/{a.total} through Box A
          {a.hitNames.length > 0 && ` (${a.hitNames.join(", ")})`}
        </div>
      )}
      <div className="muted xs" style={{ marginTop: 4 }}>
        Compare with the historical base rate below — divergence IS the signal.
      </div>
    </div>
  );
}

export default function ResultsPanel({ view, hasBoxA, hasBoxB, oniNote }: Props) {
  if (!hasBoxA) {
    return (
      <div className="panel empty">
        <div style={{ fontSize: 40 }}>📦</div>
        <b>Draw Box A on the map</b>
        <p className="muted">
          Click “▭ Draw box”, then drag a rectangle to define a transit region.
        </p>
      </div>
    );
  }

  const sa = view.summaryA!;

  return (
    <div className="panel">
      <LiveGuidanceBlock />
      {!hasBoxB ? (
        <>
          <h3>📦 Box A — Transit Region</h3>
          <div className="row">
            <div className="metric"><div className="muted xs">Total Storms</div>{sa.total}</div>
            <div className="metric"><div className="muted xs">Hurricanes</div>{sa.hurricanes}</div>
            <div className="metric"><div className="muted xs">Annual Freq</div>{sa.annual_frequency}/yr</div>
          </div>
          <div className="row">
            <div className="metric"><div className="muted xs">Tropical Storms</div>{sa.tropical_storms}</div>
            <div className="metric"><div className="muted xs">Tropical Dep.</div>{sa.tropical_depressions}</div>
          </div>
          <SampleWarn n={sa.total} />
          {/* seasonal timing — computed on every query, now actually shown:
              "WHEN does my coast get hit" is half the climatology question */}
          {Object.keys(sa.monthly_distribution).length > 0 && (
            <div className="metric" style={{ marginTop: 8 }}>
              <div className="muted xs">TRANSITS BY MONTH</div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 56, marginTop: 6 }}>
                {Object.entries(sa.monthly_distribution).map(([mon, n]) => {
                  const max = Math.max(...Object.values(sa.monthly_distribution));
                  return (
                    <div key={mon} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                      <span style={{ fontSize: 9, color: "rgba(255,255,255,0.6)" }}>{n}</span>
                      <div
                        title={`${mon}: ${n} storm${n === 1 ? "" : "s"}`}
                        style={{
                          width: "100%", borderRadius: "2px 2px 0 0",
                          height: `${Math.max(3, (n / max) * 36)}px`,
                          background: "#60A5FA",
                        }}
                      />
                      <span style={{ fontSize: 9, color: "rgba(255,255,255,0.45)" }}>{mon.slice(0, 3)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {sa.peak_storm && (
            <div className="metric" style={{ marginTop: 8 }}>
              <div className="muted xs">STRONGEST STORM</div>
              <b>
                {sa.peak_storm.name} ({sa.peak_storm.year}) — {sa.peak_storm.ss_category},{" "}
                {sa.peak_storm.peak_vmax} kt
              </b>
            </div>
          )}
          <div className="hint">Now draw <b style={{ color: "#F472B6" }}>Box B</b> — the impact region.</div>
        </>
      ) : view.chain ? (
        <>
          <h3>📊 Transit Probability</h3>
          {view.chain.count_abc != null ? <Chain3 c={view.chain} /> : <Chain2 c={view.chain} />}
        </>
      ) : null}

      {view.displayStorms.length > 0 && (
        <div className="storm-list">
          <div className="muted xs" style={{ marginTop: 12 }}>
            STORMS: {view.displayLabel} ({view.displayStorms.length})
            {!hasBoxB && <span className="muted"> · click to open</span>}
          </div>
          {/* Single Box A can return 100s of storms — make the full list
              scrollable so every one is clickable, not capped at 25. */}
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {view.displayStorms.map((s) => (
              <div
                className="storm-row clickable"
                key={s.storm_id}
                onClick={() => void useStore.getState().focusStorm(s.storm_id)}
                title="Open in Explorer"
              >
                <span>
                  {s.name === "UNNAMED" ? "Unnamed" : s.name.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())}{" "}
                  ({s.year})
                </span>
                <span className="muted">
                  {s.ss_category} · {s.peak_vmax}kt
                  {s.oni_anomaly != null ? ` · ONI ${s.oni_anomaly >= 0 ? "+" : ""}${s.oni_anomaly.toFixed(1)}` : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {oniNote && <div className="muted xs center" style={{ marginTop: 12 }}>{oniNote}</div>}
    </div>
  );
}
